import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Hash, AlertCircle } from "lucide-react";

const GEM_OPTIONS = ["GeM", "GeMNCB", "NCB", "Non-GeM", "NewNCB", "CORNCB", "NewCORNCB"];
const SUPPLY_OPTIONS = ["Supply", "Service", "Supply & Service"];

const today = () => new Date().toISOString().slice(0, 10);

const inputCls = "w-full px-3 py-2.5 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand";

function Field({ label, required, hint, error, children, span = 1, testid }) {
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""} data-testid={testid}>
      <label className="stat-label block mb-1.5">
        {label} {required && <span className="text-warn">*</span>}
      </label>
      {children}
      {hint && !error && <div className="text-[11px] text-ink-muted mt-1">{hint}</div>}
      {error && <div className="text-xs text-warn mt-1.5 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</div>}
    </div>
  );
}

export default function TenderNew() {
  const nav = useNavigate();
  const [nextSr, setNextSr] = useState(null);
  const [f, setF] = useState({
    client: "",
    location: "",
    title: "",
    gem_type: "GeM",
    deadline: "",
    tender_type: "",
    tender_type_other: "",
    supply_service: "Supply",
    contract_value: "",
    tender_no: "",
  });
  const [errors, setErrors] = useState({});
  const [submitErr, setSubmitErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tenders/next-sr-no");
        setNextSr(data.next_sr_no);
      } catch { setNextSr(null); }
    })();
  }, []);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!f.client.trim()) errs.client = "Client is required";
    if (f.client.length > 200) errs.client = "Max 200 characters";
    if (!f.location.trim()) errs.location = "Location is required";
    if (!f.title.trim()) errs.title = "Tender title is required";
    if (f.title.length > 500) errs.title = "Max 500 characters";
    if (!GEM_OPTIONS.includes(f.gem_type)) errs.gem_type = "Select a procurement category";
    if (!f.deadline) errs.deadline = "Closing date is required";
    else if (f.deadline < today()) errs.deadline = "Closing date cannot be in the past";
    if (!f.tender_type.trim()) errs.tender_type = "Type is required";
    else if (f.tender_type.length > 100) errs.tender_type = "Max 100 characters";
    if (!SUPPLY_OPTIONS.includes(f.supply_service)) errs.supply_service = "Select supply / service";
    if (!f.contract_value.trim()) errs.contract_value = "Contract value is required";
    return errs;
  };

  const isValid = Object.keys(validate()).length === 0;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr("");
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      const payload = { ...f };
      const { data } = await api.post("/tenders", payload);
      nav(`/tenders/${data.id}`);
    } catch (ex) {
      setSubmitErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="tender-new-page">
      <button onClick={() => nav(-1)} className="text-[12px] uppercase tracking-wider text-ink-muted hover:text-ink flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="border-b border-ink-line pb-6 mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Master sheet / New entry</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Create Tender</h1>
          <p className="text-sm text-ink-muted mt-2">All fields are required. Sr. No. is system-generated.</p>
        </div>
        <div className="card-flat px-4 py-3" data-testid="next-sr-no-card">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center gap-1.5">
            <Hash className="w-3 h-3" /> Sr. No.
          </div>
          <div className="font-num font-bold text-2xl text-brand mt-0.5">
            {nextSr ?? "—"}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">Auto-assigned</div>
        </div>
      </div>

      <form onSubmit={submit} className="card-flat p-6 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="tender-new-form" noValidate>
        <Field label="Client" required error={errors.client} hint="Free text · max 200 characters" testid="field-client">
          <input
            value={f.client}
            onChange={set("client")}
            maxLength={200}
            className={inputCls}
            placeholder="e.g., TS Transco"
            data-testid="tender-client-input"
          />
        </Field>

        <Field label="Location" required error={errors.location} hint="Format: City, State" testid="field-location">
          <input
            value={f.location}
            onChange={set("location")}
            className={inputCls}
            placeholder="e.g., Nalgonda, Telangana"
            data-testid="tender-location-input"
          />
        </Field>

        <Field label="Tender Title" required error={errors.title} hint={`${f.title.length} / 500 characters`} testid="field-title" span={2}>
          <textarea
            rows={2}
            value={f.title}
            onChange={set("title")}
            maxLength={500}
            className={inputCls}
            placeholder="Brief title describing the tender scope"
            data-testid="tender-title-input"
          />
        </Field>

        <Field label="GeM / Non-GeM" required error={errors.gem_type} testid="field-gem">
          <select value={f.gem_type} onChange={set("gem_type")} className={inputCls} data-testid="tender-gem-select">
            {GEM_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>

        <Field label="Closing Date" required error={errors.deadline} hint="Must be today or later · format YYYY-MM-DD" testid="field-deadline">
          <input
            type="date"
            value={f.deadline}
            onChange={set("deadline")}
            min={today()}
            className={inputCls}
            data-testid="tender-deadline-input"
          />
        </Field>

        <Field label="Tender No." error={errors.tender_no} hint="User-entered tender reference (e.g., GT-76463). Optional." testid="field-tender-no">
          <input
            value={f.tender_no}
            onChange={set("tender_no")}
            maxLength={100}
            className={inputCls}
            placeholder="e.g., GT-76463"
            data-testid="tender-no-input"
          />
        </Field>

        <Field label="Category" required error={errors.tender_type} hint={`${f.tender_type.length} / 100 characters · free text`} testid="field-type">
          <input
            value={f.tender_type}
            onChange={set("tender_type")}
            maxLength={100}
            className={inputCls}
            placeholder="e.g., Supply, Design & Supply, EPC, O&M"
            data-testid="tender-type-input"
          />
        </Field>

        <Field label="Supply / Service" required error={errors.supply_service} testid="field-supply">
          <select value={f.supply_service} onChange={set("supply_service")} className={inputCls} data-testid="tender-supply-select">
            {SUPPLY_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>

        <Field label="Budget" required error={errors.contract_value} hint='Accepts: "INR 250 Lac", "INR 12 Cr", "USD 50,000", or "Refer Document"' testid="field-contract-value" span={2}>
          <input
            value={f.contract_value}
            onChange={set("contract_value")}
            className={inputCls}
            placeholder="e.g., INR 250 Lac"
            data-testid="tender-contract-value-input"
          />
        </Field>

        {submitErr && (
          <div className="sm:col-span-2 text-sm text-warn bg-warn-soft border border-warn/30 px-3 py-2 rounded-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {submitErr}
          </div>
        )}

        <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => nav(-1)} className="px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary transition-colors">Cancel</button>
          <button
            type="submit"
            disabled={loading || !isValid}
            data-testid="tender-submit-btn"
            className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-sm transition-colors"
          >
            {loading ? "Creating…" : "Create Tender"}
          </button>
        </div>
      </form>
    </div>
  );
}
